'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Input } from '@/shared/ui/shadcn/input';
import { Button } from '@/shared/ui/shadcn/button';
import { Loader2 } from 'lucide-react';
import { capitalize } from '@/shared/lib/text';
import { useProjects, useCreateProject } from '../hooks/use-projects';
import { useNewProjectStore } from '../model/new-project-store';
import { useCheckLimit, useIsDataLoaded } from '@/app/model/stores';
import { usePaywallPayloads } from '@/features/subscription/model/subscription.selectors';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { useLimitErrorPaywall } from '@/shared/hooks';

function createNewProjectSchema(t: ReturnType<typeof useTranslations>) {
  return z.object({
    name: z.string().trim().min(1, t('newProject.errors.nameRequired')),
  });
}

type NewProjectFormData = z.infer<ReturnType<typeof createNewProjectSchema>>;

/**
 * Shared "create building" dialog. Mounted once in the authenticated layout and
 * opened from anywhere via `useNewProjectStore`. Relocated from the removed
 * Dashboard page; keeps the same RHF+Zod form, default-name generation and the
 * server-driven project-limit gate (preflight + 422 paywall routing).
 */
export function NewProjectDialog() {
  const t = useTranslations('dashboard');
  const router = useRouter();

  const isOpen = useNewProjectStore((s) => s.isOpen);
  const close = useNewProjectStore((s) => s.close);

  const { data: projectsResponse } = useProjects();
  const projects = useMemo(
    () => projectsResponse?.data || [],
    [projectsResponse]
  );

  const createProjectMutation = useCreateProject();

  // Building-limit gate. When the plan limit is reached we hand off to the
  // shared server-driven limit paywall (the same PROJECT_LIMIT_EXCEEDED payload
  // the 422 path renders) instead of showing the create form — so preflight and
  // server-rejection show identical, server-owned copy. No local limit L10n.
  const checkLimit = useCheckLimit();
  const hasSubscriptionData = useIsDataLoaded();
  const isProjectLimitReached =
    hasSubscriptionData && !checkLimit('projects', projects.length);
  const payloads = usePaywallPayloads();
  const showLimitPaywall = useLimitPaywallStore((s) => s.showPaywall);
  const projectLimitPayload = payloads?.['PROJECT_LIMIT_EXCEEDED'];
  const shouldHandOffToLimitPaywall =
    isOpen && isProjectLimitReached && Boolean(projectLimitPayload);

  const newProjectSchema = useMemo(() => createNewProjectSchema(t), [t]);
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<NewProjectFormData>({
    resolver: zodResolver(newProjectSchema),
    defaultValues: { name: '' },
  });

  const generateDefaultProjectName = useCallback(() => {
    const defaultName = t('newProject.defaultName');
    const existingNames = new Set(
      projects.map((p) => p.attributes?.name?.toLowerCase() || '')
    );
    if (!existingNames.has(defaultName.toLowerCase())) return defaultName;
    let number = 2;
    while (existingNames.has(`${defaultName.toLowerCase()} #${number}`)) {
      number++;
    }
    return `${defaultName} #${number}`;
  }, [projects, t]);

  // Reset the form with a fresh default name each time the dialog opens.
  // Re-seed the default name only when the dialog opens (not on every
  // background projects refetch — that would clobber the user's edit).
  useEffect(() => {
    if (isOpen) reset({ name: generateDefaultProjectName() });
  }, [isOpen, reset, generateDefaultProjectName]);

  // Opened while already at the building limit → close the form and surface the
  // shared server-driven limit paywall instead.
  useEffect(() => {
    if (shouldHandOffToLimitPaywall && projectLimitPayload) {
      close();
      showLimitPaywall(projectLimitPayload, 'PROJECT_LIMIT_EXCEEDED');
    }
  }, [shouldHandOffToLimitPaywall, projectLimitPayload, close, showLimitPaywall]);

  const routeLimitErrorToPaywall = useLimitErrorPaywall({
    onProjectLimit: () => close(),
  });

  const onCreateProject = useCallback(
    async (data: NewProjectFormData) => {
      // Safety net for a limit hit between opening and submitting (e.g. another
      // tab/device created a building). Hand off to the server-driven paywall;
      // if the preflight payload is somehow missing, fall through to the request
      // and let the 422's inline paywall handle it.
      if (!checkLimit('projects', projects.length) && projectLimitPayload) {
        close();
        showLimitPaywall(projectLimitPayload, 'PROJECT_LIMIT_EXCEEDED');
        return;
      }
      try {
        const response = await createProjectMutation.mutateAsync(
          capitalize(data.name.trim())
        );
        router.push(`/project/${response.data.id}`);
        close();
      } catch (err) {
        if (routeLimitErrorToPaywall(err)) return;
        setError('root', {
          message:
            err instanceof Error ? err.message : t('newProject.errorGeneric'),
        });
      }
    },
    [
      checkLimit,
      projects.length,
      projectLimitPayload,
      showLimitPaywall,
      createProjectMutation,
      router,
      close,
      routeLimitErrorToPaywall,
      setError,
      t,
    ]
  );

  return (
    <Dialog
      open={isOpen && !shouldHandOffToLimitPaywall}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) {
          close();
          reset({ name: '' });
        }
      }}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{t('newProject.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onCreateProject)}>
          <div className="space-y-4">
            <div>
              <Input
                id="project-name"
                {...register('name')}
                placeholder={t('newProject.placeholder')}
                autoFocus
              />
              {errors.name && (
                <p className="text-form-error text-destructive mt-1">
                  {errors.name.message}
                </p>
              )}
              {errors.root?.message && (
                <p className="text-form-error text-destructive mt-1">
                  {errors.root.message}
                </p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  close();
                  reset({ name: '' });
                }}
                disabled={isSubmitting}
              >
                {t('newProject.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {isSubmitting
                  ? t('newProject.creating')
                  : t('newProject.create')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
